// =============================================================================
// API client. Deliberately thin: every AI run, storage write and state merge
// happens in the Functions backend, so this file only shapes requests and
// hands back what the server said.
//
// Exports the same function names as the mobile client's services/api.js, so
// a screen ported from mobile keeps the same call sites. The differences are
// confined to the three things a browser does differently:
//   - the API base URL comes from build config, not SecureStore
//   - uploads use real File objects instead of {uri, name, type} descriptors
//   - authenticated binaries become object URLs, because an <img>/<iframe>
//     cannot carry an Authorization header
// =============================================================================
import axios from 'axios';
import { getProjectId, getProjectName } from '@shared/targetHelpers';
import { guessMime } from '@shared/uploadHelpers';
import { getSelectedTenantId } from '@mobile-services/tenantSelection';
import { getValidAccessToken, getStoredAccount } from './auth';
import { buildDefaultProject, buildProjectFromProcore } from '@shared/defaultProject';

export function getBaseURL() {
  return String(import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
}

export const api = axios.create({ timeout: 300000 });

api.interceptors.request.use(async (config) => {
  config.baseURL = getBaseURL();
  const token = await getValidAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = getSelectedTenantId();
  if (tenantId) {
    config.headers = config.headers || {};
    config.headers['x-tenant-id'] = tenantId;
  }
  return config;
});

async function authHeaders() {
  const headers = {};
  const token = await getValidAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const tenantId = getSelectedTenantId();
  if (tenantId) headers['x-tenant-id'] = tenantId;
  return headers;
}

// =============================================================================
// Tenant / projects
// =============================================================================
export async function fetchTenantContext() {
  const res = await api.get('/api/tenant/context');
  return res.data || {};
}

export async function fetchProjects() {
  try {
    const res = await api.get('/api/mobile/projects');
    if (Array.isArray(res.data?.projects)) return res.data.projects;
    if (Array.isArray(res.data)) return res.data;
  } catch (err) {
    console.warn('mobile projects fetch failed, falling back to state:', err?.message || err);
  }
  const res = await api.get('/api/state');
  return res.data?.state?.projects || res.data?.projects || [];
}

export async function createProject(name, projectNumber = '') {
  const project = buildDefaultProject(name, projectNumber);
  const res = await api.post('/api/state', { operation: 'upsertProject', project });
  return res.data?.project || project;
}

// The full project (master tree + tierDefinitions), not the lean shape
// /api/mobile/projects returns. upsertProject spreads the incoming project
// over the stored one, so an editing screen must save the whole object back.
export async function fetchFullProject(projectId) {
  const res = await api.get('/api/state');
  const projects = res.data?.state?.projects || [];
  return projects.find((p) => String(p.id || p.projectId || p.name || '') === String(projectId)) || null;
}

export async function saveProject(project) {
  const res = await api.post('/api/state', { operation: 'upsertProject', project });
  return res.data?.project || project;
}

// Permanent, and not a soft delete - the project row is filtered out of
// state.json rather than moved to the archive. Admin/owner only, enforced by
// requireAdmin on the API's lifecycle operations; the client-side gating just
// avoids offering a button that would 403.
//
// Uploaded documents are NOT removed. They live in blob storage keyed by
// project name, so they outlive the project row and would reappear against a
// new project created with the same name.
export async function deleteProject(projectId) {
  const res = await api.post('/api/state', { operation: 'deleteProject', projectId });
  return res.data;
}

// Turn chosen Procore projects into real app projects.
//
// Client-side on purpose: the project shape (tier tree, data types, tags) is
// defined by buildDefaultProject and would otherwise have to be duplicated
// server-side, where it would drift. Each one goes through the same
// upsertProject the manual create flow uses.
//
// Sequential rather than parallel - upsertProject is a read-modify-write of a
// single state blob, and firing ten at once just makes ten ETag conflicts.
export async function createProjectsFromProcore(procoreProjects = []) {
  const created = [];
  for (const source of procoreProjects) {
    const project = buildProjectFromProcore(source);
    const res = await api.post('/api/state', { operation: 'upsertProject', project });
    created.push(res.data?.project || project);
  }
  return created;
}

export async function fetchUploadTargets(project) {
  try {
    const res = await api.get('/api/mobile/upload-targets', { params: { projectId: getProjectId(project) } });
    return res.data;
  } catch {
    return null;
  }
}

// =============================================================================
// Upload
// =============================================================================
function looksLikeId(value = '') {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (/^[0-9a-f]{24,}$/i.test(raw)) return true;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw);
}

function firstNonId(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text && !looksLikeId(text)) return text;
  }
  return '';
}

// Mirrors the mobile buildUploadForm field-for-field - the API contract is the
// same, and any divergence here shows up as documents filed in the wrong
// folder or missing their tier path.
export async function buildUploadForm({ context = {}, files = [], comment = '' }) {
  const account = getStoredAccount();
  const userName = account?.name || account?.email || '';

  const project = context.project || {};
  const projectName = getProjectName(project) || context.projectName || '';
  const projectId = getProjectId(project) || context.projectId || '';

  // Location and package are independent axes. The tier selection is the
  // canonical location; block/level are just tier-1/tier-2 aliases the API
  // keeps for blob tags and Azure AI Search filters.
  const tiers = Array.isArray(context.tiers) ? context.tiers.filter((t) => t && String(t.value || '').trim()) : [];
  const block = tiers[0]?.value || '';
  const level = tiers[1]?.value || '';
  const packageName = context.packageName || '';
  const scopeType = tiers.length ? 'master' : packageName ? 'package' : 'project';
  const isPackage = !!packageName;
  // The signed-in user must never become the supplier folder; they are
  // recorded as uploadedBy instead.
  const supplierForFolder =
    firstNonId(
      context.supplier,
      context.packageSupplier,
      context.supplierName,
      context.responsibility,
      context.responsibilityName,
    ) || (isPackage ? 'supplier' : 'Project Home');

  const selectedTags = Array.isArray(context.tags) ? context.tags.map((t) => String(t || '').trim()).filter(Boolean) : [];
  const uploadTags = selectedTags.length ? selectedTags : ['Web Upload', context.uploadType || 'Documents'];
  const deviceSource = context.deviceSource || 'web';
  const uploadedAt = new Date().toISOString();

  const form = new FormData();
  // Browser File objects are already everything FormData needs - no
  // {uri, name, type} descriptor, and no manual Content-Type (the browser
  // sets the multipart boundary itself).
  for (const file of files || []) {
    if (!file) continue;
    form.append('files', file, file.name || `web-upload-${Date.now()}`);
  }

  form.append('projectId', projectId);
  form.append('projectName', projectName);
  form.append('scopeType', scopeType);
  form.append('uploadScope', scopeType);
  form.append('uploadFolderScope', packageName || 'Project Home');
  form.append('packageName', packageName);
  form.append('responsibilityId', context.responsibilityId || '');
  form.append('block', block);
  form.append('level', level);
  form.append('tiers', JSON.stringify(tiers));
  form.append('item', packageName || context.item || 'Web Upload');
  form.append('lineItemName', packageName || context.lineItemName || 'Web Upload');
  form.append('supplier', supplierForFolder || 'supplier');
  form.append('commentType', scopeType === 'master' ? 'master' : 'document');
  form.append('uploadType', context.uploadType || 'Documents');
  form.append('attachmentDescription', comment || '');
  form.append('tags', JSON.stringify(uploadTags));
  form.append('compareWithPreviousVersion', 'false');
  form.append('skipAiAnalysis', 'true');
  form.append('mobileUploadOnly', 'true');
  form.append('deviceSource', deviceSource);
  form.append('mobileUploadedAt', uploadedAt);

  form.append('context', JSON.stringify({
    source: 'ai-photo-web',
    deviceSource,
    mobileUploadedAt: uploadedAt,
    projectId,
    projectName,
    uploadScope: scopeType,
    scopeType,
    packageName,
    block,
    level,
    tiers,
    item: packageName || context.item || 'Web Upload',
    lineItemName: packageName || context.lineItemName || 'Web Upload',
    responsibilityId: context.responsibilityId || '',
    supplier: supplierForFolder || '',
    uploadedBy: userName || 'Web User',
    uploadType: context.uploadType || 'Documents',
    tags: uploadTags,
    commentTags: uploadTags,
    comment: comment || '',
  }));

  return form;
}

export async function uploadFiles(payload) {
  const form = await buildUploadForm(payload);
  const baseURL = getBaseURL();
  if (!baseURL) throw new Error('Missing API URL. Set VITE_API_URL for this build.');

  const response = await fetch(`${baseURL}/api/mobile/upload`, {
    method: 'POST',
    headers: await authHeaders(),
    body: form,
  });

  const text = await response.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }

  if (!response.ok || data?.ok === false) {
    const error = new Error(data?.details || data?.error || data?.raw || `Upload failed with status ${response.status}`);
    error.response = { status: response.status, data };
    throw error;
  }
  return data;
}

// =============================================================================
// Authenticated binaries
//
// A browser cannot attach an Authorization header to <img src> or <iframe
// src>, so the bytes are fetched here and handed back as an object URL.
// Callers must revoke the URL when done or the blob is retained for the life
// of the document.
// =============================================================================
export async function fetchBlobObjectUrl(blobName) {
  if (!blobName) return '';
  const response = await fetch(`${getBaseURL()}/api/blobView?blobName=${encodeURIComponent(blobName)}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error(`Could not load file (${response.status})`);
  return URL.createObjectURL(await response.blob());
}

export async function downloadBlob(blobName, fileName) {
  const url = await fetchBlobObjectUrl(blobName);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || String(blobName).split('/').pop() || 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the download a tick to start before releasing the blob.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// =============================================================================
// Documents + AI
// =============================================================================
export async function fetchDocuments(projectName) {
  const res = await api.get('/api/hubDocuments', { params: { project: projectName } });
  return Array.isArray(res.data?.documents) ? res.data.documents : [];
}

export async function fetchDocument({ projectName, blobName }) {
  const documents = await fetchDocuments(projectName);
  return documents.find((d) => d.blobName === blobName) || null;
}

function cleanTagList(value) {
  if (Array.isArray(value)) return value.map((x) => String(x || '').trim()).filter(Boolean);
  if (typeof value === 'string') return value.split(/[|,;\n]+/).map((x) => x.trim()).filter(Boolean);
  return [];
}

export async function runAiForAttachment(attachment, context = {}) {
  const tags = cleanTagList(attachment.tags || context.tags);
  const res = await api.post('/api/mobile/run-ai', {
    blobName: attachment?.blobName,
    blobPath: attachment?.blobName,
    name: attachment?.fileName,
    fileName: attachment?.fileName,
    contentType: attachment?.contentType,
    projectId: context.projectId || '',
    projectName: context.projectName || '',
    supplier: context.supplier || '',
    item: context.item || '',
    packageName: context.packageName || '',
    uploadType: context.uploadType || '',
    tags,
    context: { ...context, tags, blobName: attachment?.blobName, fileName: attachment?.fileName, contentType: attachment?.contentType },
  }, { timeout: 300000 });
  return res.data;
}

// Queues the same customAgentWorker job an automatic on-upload run uses, so
// the response is added alongside existing ones rather than replacing them.
export async function runAgentOnDocument({ agentId, doc, projectId, projectName }) {
  const res = await api.post('/api/custom-agent/requeue', {
    agentId,
    blobName: doc?.blobName || '',
    projectId: projectId || '',
    projectName: projectName || doc?.project || '',
    uploadType: doc?.uploadType || '',
    fileName: doc?.fileName || '',
    contentType: doc?.contentType || '',
    context: {
      projectName: projectName || doc?.project || '',
      uploadType: doc?.uploadType || '',
      documentType: doc?.uploadType || '',
      packageName: doc?.package || '',
      supplier: doc?.supplier || '',
      item: doc?.item || '',
      blobName: doc?.blobName || '',
      fileName: doc?.fileName || '',
      contentType: doc?.contentType || '',
      tiers: Array.isArray(doc?.tiers) ? doc.tiers.map((value) => ({ value })) : [],
    },
  });
  return res.data;
}

export async function submitAiCorrection({ doc, correctedFields, agentResult = null }) {
  const originalAi = agentResult
    ? (agentResult.result || agentResult.response || { summary: agentResult.summary || '' })
    : (doc?.ai?.result || { summary: doc?.ai?.summary || '' });
  const res = await api.post('/api/aiCorrectionFeedback', {
    type: 'ai_correction',
    agentId: agentResult?.agentId || '',
    agentName: agentResult?.agentName || agentResult?.label || '',
    jobId: agentResult?.jobId || doc?.ai?.jobId || '',
    blobName: doc?.blobName || '',
    fileName: doc?.fileName || '',
    originalAi,
    correctedAi: { ...originalAi, ...correctedFields },
    context: {
      projectName: doc?.project || '',
      uploadType: doc?.uploadType || '',
      packageName: doc?.package || '',
      supplier: doc?.supplier || '',
      block: doc?.block || '',
      level: doc?.level || '',
      tiers: Array.isArray(doc?.tiers) ? doc.tiers.map((value) => ({ value })) : [],
      blobName: doc?.blobName || '',
      originalFileName: doc?.fileName || '',
      contentType: doc?.contentType || '',
    },
  });
  return res.data;
}

// =============================================================================
// Chat
// =============================================================================
// Download a chat answer as Word or PDF.
//
// The blocks that were rendered on screen are sent up rather than the raw
// markdown, so the document matches what the reader actually saw and there is
// no second parser on the server to keep in step with formatAnswer.js.
export async function exportChatAnswer({ format, title, question, blocks, sources = [], projectName = '' }) {
  const baseURL = getBaseURL();
  const response = await fetch(`${baseURL}/api/chat/export`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ format, title, question, blocks, sources, projectName }),
  });

  if (!response.ok) {
    let detail = '';
    try { detail = (await response.json())?.details || ''; } catch { /* not JSON */ }
    throw new Error(detail || `Export failed (${response.status})`);
  }

  // Content-Disposition carries the server's filename; fall back to the title
  // so a browser that does not expose the header still saves something named.
  const disposition = response.headers.get('Content-Disposition') || '';
  const named = /filename="([^"]+)"/.exec(disposition)?.[1];
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = named || `${(title || 'Chat answer').slice(0, 60)}.${format === 'pdf' ? 'pdf' : 'docx'}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// includeReferences widens retrieval beyond this project to the tenant's own
// References folder and the shared standards library. Sent as a top-level
// flag rather than inside filters, because it does not narrow what comes back
// the way every other filter does - it adds to it.
  // 120 seconds was fine while the answer ceiling was 2,000 tokens. It is not
  // fine now: CHAT_WITH_DATA_MAX_TOKENS can reach 12,000, and a wide
  // comparison - every product, eight attributes - takes longer than two
  // minutes of model time including the thinking that counts against the same
  // budget. The client gave up first, which is the worst way to lose: the
  // server finishes the work and bills for it, and the reader sees a timeout.
  //
  // 300 seconds matches the instance default and sits inside the Function
  // App's own limit, so the server is now the thing that decides when to stop.
export async function askChatWithData({ question, projectId, projectName, filters = {}, includeReferences = false }) {
  const res = await api.post(
    '/api/chatWithData',
    { question, projectId, projectName, filters, includeReferences },
    { timeout: 300000 },
  );
  return res.data;
}

// =============================================================================
// Agents + AI connections
// =============================================================================
export async function fetchAgents() {
  const res = await api.get('/api/agents');
  return res.data?.agents || [];
}

export async function generateAgentDraft(description) {
  const res = await api.post('/api/generateAgent', { description }, { timeout: 120000 });
  return res.data?.draft;
}

export async function saveCustomAgent(agent) {
  const res = await api.post('/api/agentDefinitions', agent);
  return res.data?.agent;
}

export async function deleteCustomAgent(id) {
  const res = await api.delete('/api/agentDefinitions', { params: { id } });
  return res.data;
}

export async function setAgentModel(agentId, connectionId) {
  const res = await api.post(`/api/agents/${encodeURIComponent(agentId)}/model`, { connectionId });
  return res.data;
}

export async function fetchAiConnections() {
  const res = await api.get('/api/ai-connections');
  return res.data?.connections || [];
}

export async function saveAiConnection(connection) {
  const res = await api.post('/api/ai-connections', connection);
  return res.data?.connection;
}

export async function deleteAiConnection(id) {
  const res = await api.delete(`/api/ai-connections/${encodeURIComponent(id)}`);
  return res.data;
}

// =============================================================================
// Settings, team, integrations, reports
// =============================================================================
// The endpoint answers { ok, settings: {...} } - returning the envelope left
// every list undefined, so Settings rendered empty however much was saved.
export async function fetchSettings() {
  const res = await api.get('/api/hubSettings');
  return res.data?.settings || {};
}

// The rate table the cost estimates are priced from: the built-in rows with
// any tenant edit applied, each marked so the UI can show what was changed.
// Returned alongside the settings rather than derived here, because the same
// table is what the backend actually prices with.
export async function fetchAiPricing() {
  const res = await api.get('/api/hubSettings');
  return Array.isArray(res.data?.aiPricing) ? res.data.aiPricing : [];
}

export async function setAiPrice({ model, input, output }) {
  const res = await api.post('/api/hubSettings', { operation: 'setAiPrice', value: { model, input, output } });
  return Array.isArray(res.data?.aiPricing) ? res.data.aiPricing : [];
}

export async function resetAiPrice(model) {
  const res = await api.post('/api/hubSettings', { operation: 'removeAiPrice', value: { model } });
  return Array.isArray(res.data?.aiPricing) ? res.data.aiPricing : [];
}

// Settings are edited one entry at a time via an operation, not by posting a
// whole settings object: addUploadType / removeUploadType / addDocumentTag /
// removeDocumentTag. Posting the object was silently ignored by the server.
// Returns the updated settings so the caller renders what was actually stored.
export async function updateSettings(operation, value) {
  const res = await api.post('/api/hubSettings', { operation, value });
  return res.data?.settings || {};
}

// Who the caller is and what they may do. Used to hide controls the server
// would reject anyway - never as the enforcement itself, which lives in the
// API's adminGuard.
export async function fetchMe() {
  const res = await api.get('/api/me');
  return res.data;
}


// =============================================================================
// Tenant branding — the logo drawn at the top of generated reports
//
// The image itself lives in the tenant's own container; the report engine
// loads it at render time (pdfLibReportEngine.js), so uploading one here is
// all that is needed for it to appear. Reading the status is open to any
// member; changing it is admin-only server-side.
// =============================================================================

export async function fetchTenantLogo() {
  const res = await api.get('/api/tenant-branding/logo');
  return res.data || { hasLogo: false };
}

export async function uploadTenantLogo(file) {
  const form = new FormData();
  form.append('logo', file);
  // Content-Type is deliberately left unset so the browser adds the multipart
  // boundary; setting it by hand produces a body the API cannot parse.
  const res = await api.post('/api/tenant-branding/logo', form);
  return res.data;
}

export async function deleteTenantLogo() {
  const res = await api.delete('/api/tenant-branding/logo');
  return res.data;
}

export async function fetchTeamMembers() {
  const res = await api.get('/api/tenant/members');
  return res.data;
}

export async function inviteTeamMember({ email, role, org, orgType, packages }) {
  const res = await api.post('/api/tenant/members', { email, role, org, orgType, packages });
  return res.data;
}

export async function removeTeamMember(email) {
  const res = await api.delete('/api/tenant/members', { params: { email } });
  return res.data;
}

// Activates the caller's own seat from an invite token. Unlike every other
// call here, the caller is by definition not yet a member of the tenant -
// membership is what this grants, so it must not be gated on already having
// it. Errors are returned rather than thrown: "wrong account" and "expired"
// are ordinary outcomes this page has specific words for, not failures.
export async function acceptInvite(token) {
  try {
    const res = await api.post('/api/tenant/accept', { token });
    return res.data;
  } catch (err) {
    const data = err?.response?.data;
    if (data) return { ...data, status: err.response.status };
    throw err;
  }
}

export async function fetchIntegrationStatus() {
  const res = await api.get('/api/integrations/status');
  return res.data || {};
}

// Asks the API for the provider's authorize URL, then the caller navigates to
// it. Deliberately a POST rather than navigating to the API itself.
//
// The API also exposes a GET that 302s straight to the provider, but reaching
// it means a full-page navigation, which cannot carry the Authorization header
// - so the token has to travel as a query param, where it ends up in browser
// history, Referer headers and the platform's request logs. Worse, fetching
// that token can trigger MSAL's interactive re-auth redirect, which returns to
// the app's origin and abandons the connect flow entirely: the user lands back
// on the home page having never reached the provider.
//
// Posting avoids both. The token goes in the header as usual, and nothing
// navigates until the provider's own URL is in hand, so there is no in-flight
// navigation for a re-auth redirect to pre-empt.
export async function startIntegrationConnect(providerId) {
  const res = await api.post(`/api/integrations/${encodeURIComponent(providerId)}/start`, {
    returnTo: `${window.location.origin}/connections`,
  });
  const url = res.data?.authorizeUrl || '';
  if (!url) throw new Error('The server did not return an authorize URL.');
  return url;
}

// Re-index one document, for when it uploaded but its text never reached the
// search index. Not admin-only: the server gates a single named document on
// being signed in, because the alternative self-service fix is uploading the
// same file again - same cost, plus a duplicate.
export async function reindexDocument(blobName) {
  const res = await api.post('/api/maintenance/reindex', { blobName });
  return res.data;
}

export async function disconnectIntegration(providerId) {
  const res = await api.post(`/api/integrations/${encodeURIComponent(providerId)}/disconnect`, {});
  return res.data;
}

// =============================================================================
// Procore data operations
//
// Connecting is generic (integrations/*); these are Procore's own resources,
// so they stay on Procore-specific routes. Importing the project list is
// admin-only server-side - it writes tenant state and picks which Procore
// company to read from. Pushing a document is ordinary work any member can do.
// =============================================================================

// Companies this connection can see. Not the same as companies the app is
// installed on - Procore only reveals that by refusing a real request - so the
// picker lists all of them and lets the admin choose.
export async function fetchProcoreCompanies() {
  const res = await api.get('/api/procore/companies');
  return res.data || {};
}

export async function importProcoreProjects(companyId = '') {
  const res = await api.post('/api/procore/sync', companyId ? { companyId } : {});
  return res.data;
}

export async function fetchProcoreProjects() {
  const res = await api.get('/api/procore/projects');
  return res.data || {};
}

// blobName identifies the document already in this tenant's storage - the file
// itself is never re-uploaded from the client, the API streams it straight
// from blob storage to Procore.
export async function pushDocumentToProcore({ procoreProjectId, blobName, fileName, kind, metadata }) {
  const path = kind === 'photo' ? '/api/procore/pushPhoto' : '/api/procore/pushDrawing';
  const res = await api.post(path, { procoreProjectId, blobName, fileName, metadata: metadata || {} });
  return res.data;
}

// One folder's immediate contents, for browsing before importing anything.
//
// GET is inert by design - the API downloads, extracts and indexes nothing on
// this path - so a person can open folders freely without each click costing
// an import. Pass no folderId for the project's root.
export async function listProcoreFolder({ projectId, folderId = '' }) {
  const params = new URLSearchParams({ projectId: String(projectId || '') });
  if (folderId) params.set('folderId', String(folderId));
  const res = await api.get(`/api/procore/documents/import?${params.toString()}`);
  return res.data || {};
}

// Import the named files. Admin-only server-side, and it is the opposite of
// inert: each file is downloaded, stored in this tenant's own container,
// extracted and indexed.
//
// The reply reports every file separately - { id, name, ok, chars, reason } -
// because the interesting cases are per-file rather than per-request. A
// scanned drawing with no text layer comes back ok:false, reason
// "no_text_to_index", and the person needs to see that rather than wonder why
// chat cannot find it later.
// Which Procore files are already in this project, by Procore file id.
//
// Read from the stored documents' own procorefileid rather than a ledger, so
// it cannot disagree with what is actually held. Fetched once per card and
// reused as the person moves around the folder tree.
export async function fetchImportedProcoreFileIds({ projectId, projectName = '' }) {
  const res = await api.get('/api/procore/documents/import', {
    params: { projectId, mode: 'imported', ...(projectName ? { projectName } : {}) },
  });
  return res.data || {};
}

// The classification is sent with the import, so an imported document is filed
// the way an uploaded one is - upload type, package, location tiers and tags.
// Each field is omitted when empty rather than sent blank, because the API
// falls back to its old "Procore" defaults on an absent value and sending ""
// would defeat that for anyone importing without choosing anything.
export async function importProcoreDocuments({
  projectId,
  fileIds = [],
  folderIds = [],
  projectName = '',
  uploadType = '',
  packageName = '',
  supplier = '',
  tiers = [],
  tags = [],
}) {
  const res = await api.post('/api/procore/documents/import', {
    projectId,
    fileIds,
    // The folders the picks came from. Without these the API walks the whole
    // project to locate files it was already given the ids of.
    ...(folderIds.length ? { folderIds } : {}),
    ...(projectName ? { projectName } : {}),
    ...(uploadType ? { uploadType } : {}),
    ...(packageName ? { packageName } : {}),
    ...(supplier ? { supplier } : {}),
    ...(tiers.length ? { tiers } : {}),
    ...(tags.length ? { tags } : {}),
  });
  return res.data || {};
}

// Send one of this tenant's own documents back to Procore's Documents tool.
//
// The counterpart to the import. folderId is where it lands; omitting it means
// the project root, which is a destination rather than a missing value. The
// file is never re-uploaded from the client - the API streams it from blob
// storage to Procore, the way the drawing and photo pushes do.
export async function sendDocumentToProcoreFolder({ procoreProjectId, blobName, fileName, folderId = '', description = '' }) {
  const res = await api.post('/api/procore/documents/push', {
    procoreProjectId,
    blobName,
    fileName,
    ...(folderId ? { folderId } : {}),
    ...(description ? { description } : {}),
  });
  return res.data || {};
}

// Keep a chat answer under Reports, rather than downloading it.
//
// The same builders produce the same document either way; the difference is
// where it lands. A saved answer is stored under the app's own underscore
// prefix, so it is not a document in the library and is never indexed - an
// answer must not become a source for later answers.
export async function saveChatAnswerToProject({ format = 'pdf', title = '', question, text = '', blocks, sources = [], projectId = '', projectName = '', model = '' }) {
  const res = await api.post('/api/saved/answers', {
    // The model that produced the answer, carried from the chat response so a
    // kept answer records what concluded it and not only what it concluded.
    // A verification copies this in when the answer is signed off.
    format, title, question, text, blocks, sources, projectId, projectName, model,
  });
  return res.data || {};
}

// Sign a saved answer or report off: a named person states they have checked
// it. The name comes from the session server-side, never from here - a
// signature the client can type is not a signature.
export async function verifySavedFile({ blobName, statement = '' }) {
  const res = await api.post('/api/saved/verify', { blobName, ...(statement ? { statement } : {}) });
  return res.data || {};
}

// The sign-off as it stands, including whether the file still matches the one
// that was signed. The listings carry enough to draw a badge; this is for when
// somebody opens the detail.
export async function fetchSavedVerification(blobName) {
  const res = await api.get('/api/saved/verify', { params: { blobName } });
  return res.data || {};
}

// The answer a saved file was rendered from, so Reports can show it with the
// same component Chat uses rather than a second renderer that would drift.
// Bind several kept answers into one report.
//
// The sections are parsed client-side with the same formatAnswer the screen
// and the exports use, so there is exactly one markdown parser in the product.
// The API decides how they fit together - citation renumbering, the merged
// source list and the executive summary all have to agree, and that is one
// decision rather than three.
export async function buildSavedReport({ projectId = '', projectName = '', title = '', format = 'pdf', sections = [] }) {
  const res = await api.post('/api/saved/reports', { projectId, projectName, title, format, sections });
  return res.data || {};
}

export async function fetchSavedReports(projectId = '') {
  const res = await api.get('/api/saved/reports', { params: projectId ? { projectId } : {} });
  return res.data || {};
}

export async function fetchSavedAnswerRecord(recordName) {
  const response = await fetch(`${getBaseURL()}/api/blobView?blobName=${encodeURIComponent(recordName)}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error(`Could not load the saved answer (${response.status})`);
  return response.json();
}

export async function fetchSavedAnswers(projectId = '') {
  const res = await api.get('/api/saved/answers', { params: projectId ? { projectId } : {} });
  return res.data || {};
}

// Email a saved report or answer to people on the project.
//
// Recipients are addresses from the workspace roster, not free text: the API
// refuses anything else, so the picker and the server agree on one list rather
// than the client being trusted to stay inside it. The file is never uploaded
// from here - the API reads it from blob storage and attaches it, the way the
// Procore push does.
export async function shareSavedFile({ blobName, to = [], note = '' }) {
  const res = await api.post('/api/saved/share', { blobName, to, ...(note ? { note } : {}) });
  return res.data || {};
}

export async function saveIntegrationCredentials(providerId, credentials) {
  const res = await api.post(`/api/integrations/${encodeURIComponent(providerId)}/credentials`, credentials);
  return res.data;
}

// Clears a tenant's own client id/secret, reverting to whatever the deployment
// provides (usually nothing). The secret is never readable once saved, so
// replacing it means entering both fields again rather than editing one.
export async function clearIntegrationCredentials(providerId) {
  const res = await api.delete(`/api/integrations/${encodeURIComponent(providerId)}/credentials`);
  return res.data;
}

// Report generation is queued server-side and polled, so a long report never
// blocks the request.
export async function requestReportSpec({ description, projectName }) {
  const res = await api.post('/api/reportSpecRequest', { description, projectName });
  return res.data;
}

export async function fetchReportSpecStatus(jobId) {
  const res = await api.get('/api/reportSpecStatus', { params: { jobId } });
  return res.data;
}

export async function requestReportDownload(payload) {
  const res = await api.post('/api/reportDownloadRequest', payload);
  return res.data;
}

export async function fetchReportDownloadStatus(jobId) {
  const res = await api.get('/api/reportDownloadStatus', { params: { jobId } });
  return res.data;
}

// The narrative/PDF family, separate from the documents-package (ZIP) family
// above. Both are request-then-poll and both return the finished file as
// `sasUrl` when status is "complete".
export async function requestPdfReportDownload(payload) {
  const res = await api.post('/api/reportPdfDownloadRequest', payload);
  return res.data;
}

export async function fetchPdfReportDownloadStatus(jobId) {
  const res = await api.get('/api/reportPdfDownloadStatus', { params: { jobId } });
  return res.data;
}

// The drawing register and the drawing-comparison report are both built by
// the SAME endpoint as the documents package - runDownloadReportJob branches
// on flags in the body, so they are not separate routes. Nothing was wrong
// with them; no client was asking for them. Payload mirrors what the original
// Hub sends, including the redundant-looking reportType alongside the boolean
// (the backend accepts either, and older deployments read one or the other).
export async function requestDrawingRegister({ projectId, projectName, format = 'pdf' }) {
  const res = await api.post('/api/reportDownloadRequest', {
    projectId,
    projectName,
    drawingRegisterReport: true,
    reportType: 'drawing_register',
    pdfComparisonScope: 'all_time',
    format,
  });
  return res.data;
}

export async function requestDrawingComparison({ projectId, projectName, format = 'pdf' }) {
  const res = await api.post('/api/reportDownloadRequest', {
    projectId,
    projectName,
    pdfComparisonReport: true,
    reportType: 'pdf_comparison',
    pdfComparisonScope: 'all_time',
    format,
  });
  return res.data;
}

// Poll a report status endpoint until it settles. Mirrors waitForReport in the
// mobile client so both drive the same request-then-poll contract, and lets a
// caller write the whole flow as straight-line awaits instead of nesting
// setInterval callbacks that capture stale state.
export async function waitForReport(pollFn, jobId, { intervalMs = 3000, timeoutMs = 300000 } = {}) {
  const start = Date.now();
  for (;;) {
    const status = await pollFn(jobId);
    if (status?.status === 'complete' || status?.status === 'failed') return status;
    if (Date.now() - start > timeoutMs) throw new Error('Timed out waiting for the report to finish.');
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

// Turns a resolved report spec into whichever report request actually matches
// it. A table spec goes to the documents endpoint as a custom table
// definition; anything else is a narrative and goes to the PDF endpoint.
// Posting the raw spec to either one does nothing - neither reads a `spec`
// field. Mirrors requestReportFromSpec in the mobile client.
export async function requestReportFromSpec({ spec, projectId, projectName }) {
  if (spec?.reportType === 'table') {
    const res = await api.post('/api/reportDownloadRequest', {
      projectName,
      customTableReport: true,
      // Carry the spec's own row source through. Hardcoding 'documents' here
      // made every generated table a document register, so a request for the
      // data inside an analysis - "all design stages for MSA" - came back as a
      // list of files with document columns.
      reportDefinition: {
        rowSource: spec.table?.rowSource === 'agentData' ? 'agentData' : 'documents',
        columns: spec.table?.columns || [],
        description: spec.table?.description || spec.title || '',
        name: spec.title,
      },
      documentTags: spec.tags || [],
      documentTypes: spec.uploadTypes || [],
      dateFrom: spec.dateRange?.mode === 'range' ? spec.dateRange.from : '',
      dateTo: spec.dateRange?.mode === 'range' ? spec.dateRange.to : '',
    });
    return { ...res.data, kind: 'documents' };
  }

  const res = await api.post('/api/reportPdfDownloadRequest', {
    projectId,
    projectName,
    reportKind: 'project_report',
    title: spec?.title,
    narrativeInstructions: spec?.narrative?.instructions || '',
    reportDepth: spec?.narrative?.depth === 'in_depth' ? 'in_depth' : 'standard',
  });
  return { ...res.data, kind: 'pdf' };
}

export default api;

// =============================================================================
// Public (unauthenticated) endpoints
// =============================================================================

// Deliberately uses fetch rather than the `api` client: that client's request
// interceptor asks for an access token, and this call is made by a visitor who
// has not signed in and has no tenant. Parsed defensively because in local dev
// the backend is often absent and returns an empty body, which would otherwise
// throw "Unexpected end of JSON input" rather than a message worth showing.
export async function startCheckout({ plan, email, orgName }) {
  const baseURL = getBaseURL();
  if (!baseURL) throw new Error('Missing API URL. Set VITE_API_URL for this build.');
  const res = await fetch(`${baseURL}/api/billing/checkout`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ plan, email, orgName }),
  });
  const text = await res.text().catch(() => '');
  if (!text) {
    if (!res.ok) throw new Error('billing_unreachable');
    return {};
  }
  try { return JSON.parse(text); } catch { return {}; }
}

// The tenant's plan, its limits and how much of each is used. Resolved from
// the signed-in identity server-side, so it takes no arguments.
export async function fetchEntitlements() {
  const res = await api.get('/api/tenant/entitlements');
  return res.data;
}

// Which projects may be edited while the tenant is over their plan's project
// limit. `restricted: false` is the normal case - inside the allowance there is
// nothing to choose and nothing to show.
export async function fetchActiveProjects() {
  const res = await api.get('/api/tenant/active-projects');
  return res.data;
}

// Owner/admin only, enforced server-side. More ids than the plan allows are
// capped rather than rejected; the response says how many were dropped.
export async function saveActiveProjects(projectIds) {
  const res = await api.post('/api/tenant/active-projects', { projectIds });
  return res.data;
}

// Opens Stripe's Customer Portal, where a customer changes plan, updates the
// card, downloads invoices or cancels. Owner/admin only, enforced server-side.
//
// The tenant is taken from the signed-in caller, so nothing identifying is
// sent; returnUrl is a PATH, and the API refuses anything absolute.
export async function openBillingPortal(returnUrl = '/settings') {
  const res = await api.post('/api/billing/portal', { returnUrl });
  return res.data;
}

// What the public pricing page needs before anyone signs in - currently the
// trial length, so the page's promise and Checkout's behaviour come from the
// same setting rather than being written down twice.
//
// Anonymous, and deliberately forgiving: a visitor should see the price list
// whether or not this answers, so a failure returns an empty object and the
// page simply says nothing about a trial.
export async function fetchBillingConfig() {
  const baseURL = getBaseURL();
  if (!baseURL) return {};
  try {
    const res = await fetch(`${baseURL}/api/billing/config`);
    const text = await res.text().catch(() => '');
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

// Polled by the post-checkout page while the tenant is provisioned.
export async function fetchProvisioningStatus(email) {
  const baseURL = getBaseURL();
  if (!baseURL) throw new Error('Missing API URL. Set VITE_API_URL for this build.');
  const res = await fetch(`${baseURL}/api/tenant/status?email=${encodeURIComponent(email)}`);
  const text = await res.text().catch(() => '');
  try { return text ? JSON.parse(text) : {}; } catch { return {}; }
}
